/**
 * DeskRegistryStripMolecule — the Desk's "Registry strip": one entry per `STUDIO_BUILDERS` builder
 * with its per-type total/draft counts.
 *
 * Pre-create check (atomic-design Rule 3): `SegmentedControlAtom` and `ChipAtom` were considered.
 * `SegmentedControlAtom` models single-select mode switching, not a navigable registry of builders,
 * so it was rejected; `ChipAtom` IS reused here for the count chips. Only the
 * `STUDIO_BUILDERS`-driven strip layout is new.
 */

import React from 'react';
import { useTranslation } from 'localization';
import { ChipAtom } from 'design-system/my-lib';
import type { StudioDesk } from 'types/studio';
import { STUDIO_BUILDERS, StudioBuilder } from './builders';
import './DeskRegistryStripMolecule.scss';

export interface DeskRegistryStripMoleculeProps {
  counts: StudioDesk['counts'];
  /** True when the `counts` sub-source failed — no count chip renders rather than a fabricated zero. */
  countsUnavailable?: boolean;
  onOpen: (builder: StudioBuilder) => void;
  className?: string;
}

/** Sums `counts[type]` across every `template.type` a builder owns — the bridge the ratified
 * decision added `StudioBuilder.templateTypes` for (counts is keyed by type, this registry by
 * builder id). */
function countForBuilder(builder: StudioBuilder, counts: StudioDesk['counts']): { total: number; drafts: number } | undefined {
  if (!builder.templateTypes || builder.templateTypes.length === 0) return undefined;
  let total = 0;
  let drafts = 0;
  let any = false;
  for (const type of builder.templateTypes) {
    const bucket = counts[type];
    if (!bucket) continue;
    any = true;
    total += bucket.total;
    drafts += bucket.drafts;
  }
  return any ? { total, drafts } : undefined;
}

/**
 * Registry strip (spec SC1/UI Components table) — the demoted builder gallery, one compact entry
 * per `STUDIO_BUILDERS` regardless of desk-call success (SC1's "the Registry strip still renders
 * from `STUDIO_BUILDERS`" clause). Scaffolded builders never show a count (pre-existing rule from
 * `StudioHomePage`); when `countsUnavailable`, no builder shows a count — a governance surface
 * that displays fabricated numbers is worse than one that displays none.
 */
const DeskRegistryStripMolecule: React.FC<DeskRegistryStripMoleculeProps> = ({
  counts,
  countsUnavailable = false,
  onOpen,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={['desk-registry-strip', className].filter(Boolean).join(' ')}
      data-testid="desk-registry-strip"
    >
      {STUDIO_BUILDERS.map((builder) => {
        const count = builder.status !== 'scaffolded' && !countsUnavailable ? countForBuilder(builder, counts) : undefined;
        return (
          <button
            key={builder.id}
            type="button"
            className="desk-registry-strip__entry"
            data-testid={`desk-registry-strip-entry-${builder.id}`}
            onClick={() => onOpen(builder)}
          >
            <i className={`${builder.icon} desk-registry-strip__icon`} aria-hidden="true" />
            <span className="desk-registry-strip__label">{t(builder.labelKey)}</span>
            {builder.status === 'scaffolded' && (
              <ChipAtom
                className="desk-registry-strip__chip"
                label={t('studio.home.scaffoldedChip')}
                data-testid={`desk-registry-strip-scaffolded-${builder.id}`}
              />
            )}
            {count && (
              <ChipAtom
                className="desk-registry-strip__chip"
                label={t('studio.desk.registry.count', { total: count.total, drafts: count.drafts })}
                data-testid={`desk-registry-strip-count-${builder.id}`}
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default DeskRegistryStripMolecule;
